import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { Sprint } from '../../api/types';
import { defaultOpenKeys, isSectionOpen, UNASSIGNED_SECTION_KEY, type SectionKey } from './sprint-sections';

/**
 * 🔴 COLLAPSE STATE LIVES IN THE URL, so a shared link opens the same view.
 *
 * ⭐ AS OVERRIDES, IN TWO PARAMETERS, NOT AS A SNAPSHOT OF EVERY SECTION:
 *
 *     /backlog?project=1                       → the sensible default (see `defaultOpenKeys`)
 *     /backlog?project=1&collapsed=12          → the default, but sprint 12 was closed by hand
 *     /backlog?project=1&opened=9,backlog      → the default, plus closed sprint 9 opened by hand
 *
 * Why two sets rather than one list of "the open sections": the default is not a constant. It depends
 * on which sprint is ACTIVE today. A snapshot link written this week would keep last week's sprint
 * open after it closed, and would keep the new active sprint SHUT — the link would age into exactly
 * the everything-collapsed page this design exists to avoid. Recording only what a human deliberately
 * toggled means the link carries the intent and the default keeps doing its job around it.
 *
 * ⭐ `replace: true` on every write, for the same reason `toggleExpanded` uses it: collapsing a
 * section is a change of VIEW, not of place. Pushing history would make Back re-open sections one at
 * a time instead of leaving the backlog.
 *
 * ⚠️ A key is a sprint id (digits) or the literal `backlog`. Anything else in the URL is IGNORED
 * rather than trusted — the parameter is user-editable text and a stray value must not become a
 * section nobody can find.
 */

const OPENED_PARAM = 'opened';
const COLLAPSED_PARAM = 'collapsed';

export function isValidSectionKey(value: string): boolean {
  return value === UNASSIGNED_SECTION_KEY || /^\d+$/.test(value);
}

export function parseSectionKeys(raw: string | null | undefined): Set<SectionKey> {
  if (!raw) return new Set();
  return new Set(raw.split(',').filter(isValidSectionKey));
}

export interface SectionOverrides {
  opened: Set<SectionKey>;
  collapsed: Set<SectionKey>;
}

export function readOverrides(params: URLSearchParams): SectionOverrides {
  return {
    opened: parseSectionKeys(params.get(OPENED_PARAM)),
    collapsed: parseSectionKeys(params.get(COLLAPSED_PARAM)),
  };
}

/**
 * The URL after one section is set open or closed.
 *
 * ⛔ A key is never in both sets: it is written to one and removed from the other in the same step.
 * Two sources of truth for one boolean is how "collapsed wins over opened" becomes a rule somebody
 * has to remember, and then forgets.
 *
 * ⭐ Returns a NEW URLSearchParams — the caller's copy is never mutated.
 */
export function writeSectionState(
  params: URLSearchParams,
  keys: readonly SectionKey[],
  open: boolean,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const { opened, collapsed } = readOverrides(next);
  const add = open ? opened : collapsed;
  const remove = open ? collapsed : opened;

  for (const key of keys) {
    if (!isValidSectionKey(key)) continue;
    add.add(key);
    remove.delete(key);
  }

  setOrDelete(next, OPENED_PARAM, [...opened].join(','));
  setOrDelete(next, COLLAPSED_PARAM, [...collapsed].join(','));
  return next;
}

function setOrDelete(params: URLSearchParams, key: string, value: string): void {
  if (value === '') params.delete(key);
  else params.set(key, value);
}

export interface SectionCollapse {
  isOpen: (key: SectionKey) => boolean;
  toggle: (key: SectionKey) => void;
  setAll: (keys: readonly SectionKey[], open: boolean) => void;
}

export function useSectionCollapse(sprints: readonly Sprint[]): SectionCollapse {
  const [params, setParams] = useSearchParams();

  const defaults = useMemo(() => defaultOpenKeys(sprints), [sprints]);
  const overrides = useMemo(() => readOverrides(params), [params]);

  const isOpen = useCallback(
    (key: SectionKey) => isSectionOpen(key, defaults, overrides),
    [defaults, overrides],
  );

  const setAll = useCallback(
    (keys: readonly SectionKey[], open: boolean) => {
      setParams(writeSectionState(params, keys, open), { replace: true });
    },
    [params, setParams],
  );

  const toggle = useCallback(
    (key: SectionKey) => {
      setParams(writeSectionState(params, [key], !isOpen(key)), { replace: true });
    },
    [params, setParams, isOpen],
  );

  return { isOpen, toggle, setAll };
}
